import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
} from '@mui/material';
import {
  NewReleases as ChangelogIcon,
  Close as CloseIcon,
  BugReport as BugIcon,
  Dashboard as DashboardIcon,
  Assignment as TsiIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  GitHub as RepositoriesIcon,
  Security as SecurityIcon,
  Speed as PerformanceIcon,
  LocalOffer as ReleaseIcon,
  Science as TestsIcon,
  AutoAwesome as AutomationIcon,
  Checklist as RecommendedTestsIcon,
  Engineering as EngineeringIcon,
  HealthAndSafety as HealthIcon,
  MonitorHeart as OpsCenterIcon,
  Gavel as ComplianceIcon,
  Shield as HipaaCuiIcon,
  Computer as TestInstanceIcon,
  Description as WSRIcon,
  Summarize as SummarizeIcon,
  CalendarMonth as CalendarMonthIcon,
  SwapHoriz as MigrationsIcon,
  TableChart as ScrumMetricsIcon,
  FactCheck as ShaIcon,
  FindInPage as PlatformInvestigationIcon,
  Shield as GuardrailIcon,
  AccountTree as CoverageIcon,
  SmartToy as AIToolkitIcon,
  HealthAndSafety as ReliabilityIcon,
  SupportAgent as CustomerIssueIcon,
  Policy as AuditConsoleIcon,
  Article as ConfidencePlanIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';
import { isAuthenticated, isAdmin, logout, getCurrentUser, hasOperationalExcellenceAccess } from '../../services/authService';
import { format } from 'date-fns';
import apiService, { getActiveSessionsCount, get, getTeamsWithStreams } from '../../services/apiService';
import ActiveSessionsDialog from '../common/ActiveSessionsDialog';
import ProductionDefects from './ProductionDefects';
import BugTrendsView from '../workitems/BugTrendsView';
import TsiDashboard from '../dashboard/TsiDashboard';
import RepositoriesView from '../quality-assurance/RepositoriesView';
import SecurityPage from '../security/SecurityPage';
import HipaaCuiDashboard from '../compliance/HipaaCuiDashboard';
import ComplianceDeliveryLanding from '../compliance/ComplianceDeliveryLanding';
import H22026Dashboard from '../compliance/h2-planning/H22026Dashboard';
import ReliabilityDashboard from '../reliability/ReliabilityDashboard';
import PerformanceView from '../performance/PerformanceView';
import ReleaseComparisonTab from '../quality-assurance/ReleaseComparisonTab';
import PublicReleasesView from './PublicReleasesView';
import Tests from './Tests';
import EpicGanttChart from '../dashboard/EpicGanttChart';
import AutomationTab from '../quality-assurance/AutomationTab';
import RecommendedTestsView from '../quality-assurance/RecommendedTestsView';
import EngineeringProcessesView from '../engineering-processes/EngineeringProcessesView';
import AIToolkitView from '../ai-toolkit/AIToolkitView';
import OpsCenter from '../ops-center/OpsCenter';
import Compliance from '../compliance/Compliance';
import QAHealthDashboard from '../dashboard/QAHealthDashboard';
import TestPlanGenerator from './TestPlanGenerator';
import TestInstancesView from '../quality-assurance/TestInstancesView';
import WSRView from './WSRView';
import PlanningView from './PlanningView';
import WorkItemsView from '../workitems/WorkItemsView';
import ReleaseCalendarView from './ReleaseCalendarView';
import CoverageLanding from '../coverage/CoverageLanding';
import CoverageTreeView from '../coverage/CoverageTreeView';
import InternalBugsPublic from './InternalBugsPublic';
import MigrationsView from '../migrations/MigrationsView';
import SHAView from '../sha/SHAView';
import PlatformInvestigationView from '../platform-investigation/PlatformInvestigationView';
import OperationalExcellenceHub from '../operational-excellence/OperationalExcellenceHub';
import AuditConsolePage from '../operational-excellence/audit-console/AuditConsolePage';
import ConfidencePlanPage from '../operational-excellence/confidence-plan/ConfidencePlanPage';
import GuardrailView from '../guardrail/GuardrailView';
import SprintMetricsView from '../workitems/SprintMetricsView';
import PendingRequestsBanner from '../auth/PendingRequestsBanner';

const CHANGELOG = [
  {
    date: 'Sep 1, 2026',
    entries: [
      { area: 'Service Operations', text: 'Health Check: Hazelcast cluster size validation — reads Cluster size from getCBV.action and flags any instance where it is not 2; results show ✓ 2 nodes or ✗ N nodes per instance.' },
      { area: 'Service Operations', text: 'Health Check: All Hosts validation — checks that the All Hosts field contains hostnames (not IP addresses) and each hostname matches the server-prefix pattern derived from the machine name (e.g. p1-ap10[ab].*).' },
      { area: 'Service Operations', text: 'Health Check: Redirect domain auto-update — when getCBV.action redirects to a different domain (e.g. parauco.highq.com → legal.parauco.com), the new domain is persisted to the database immediately and flagged inline in results.' },
      { area: 'Service Operations', text: 'Health Check: Re-run failed — now pulls fresh instance domains from the database (picks up redirect fixes) and correctly decrements partial/error counters before re-incrementing.' },
    ],
  },
  {
    date: 'Aug 18, 2026',
    entries: [
      { area: 'Compliance Delivery', text: 'New landing page — sidebar "HIPAA/CUI" renamed to "Compliance Delivery"; clicking opens a full-height two-column page with HIPAA/CUI — H1-2026 and Compliance — H2-2026 cards.' },
      { area: 'Compliance Delivery', text: 'New H2-2026 workstream — "Compliance H2-2026 Delivery" dashboard with Dashboard, Product Engineering, Assets and Delivery tabs across two pillars: Operational Compliance and Azure Gov Compliance.' },
      { area: 'Compliance Delivery', text: 'Product Engineering tab: ADO features grouped by initiative (compliance-category-<alias> tags), expandable rows with backlog items, pillar filter, search, and expand/collapse all.' },
      { area: 'Compliance Delivery', text: 'Delivery tab: quarter/half-year filter (H1/H2/Q3/Q4) with per-pillar feature tables showing progress, status and story points.' },
      { area: 'Compliance Delivery', text: 'Admin — H2-2026 Planning tab: configure Epic IDs per pillar, sync ADO tags, and manage initiative sub-categories with CRUD dialogs.' },
    ],
  },
  {
    date: 'Aug 12–14, 2026',
    entries: [
      { area: 'Guardrail', text: 'Compact stat cards — count + % inline, band breakdown in a 2×2 grid, reduced padding.' },
      { area: 'Guardrail', text: 'New Classification filter (Overall / Per Site / Operational) matching Excel column groups — narrows the table to relevant columns.' },
      { area: 'Guardrail', text: 'Client filter with absolute thresholds: Critical >1 000 pts / High >500 / Medium >100 / Low ≤100.' },
      { area: 'Guardrail', text: 'Added Same Machine Client Count column — shows how many other instances share the same app server.' },
      { area: 'Guardrail', text: 'Fixed Excel upload parser for the new 4-row header format (group labels on row 3, column names on row 4).' },
      { area: 'Guardrail', text: 'Removed DocuSign/Site column — no longer present in the source data.' },
      { area: 'Reliability', text: 'Pillar Progress cards now show completed/total feature count alongside story points (e.g. 3/10 features).' },
    ],
  },
  {
    date: 'Aug 11, 2026',
    entries: [
      { area: 'Guardrail', text: 'New Criteria Breakdown tab — shows which metric thresholds breach the most clients per tier, with collapsible T1/T2/T3 sections and progress bars.' },
      { area: 'Guardrail', text: 'Dashboard auto-refreshes when tier configuration is saved — no manual page reload needed.' },
      { area: 'Guardrail', text: 'Fixed tier classification: T3 is now the catch-all. Previously ~733 clients were left unclassified.' },
    ],
  },
  {
    date: 'Aug 7–10, 2026',
    entries: [
      { area: 'Guardrail', text: 'Export to Excel button — exports the full filtered client list with all active filters applied.' },
      { area: 'Guardrail', text: 'Column filters — filter any metric column by exact value, range, or text match.' },
      { area: 'Guardrail', text: 'Stat cards enriched with % of total and 4-band points breakdown (Critical / High / Medium / Low) per tier.' },
      { area: 'Guardrail', text: 'Table numbers use compact suffixes (2.1M, 71K); storage columns show GB/TB.' },
      { area: 'Guardrail', text: 'Added Total Monthly API Calls (Datadog) column.' },
      { area: 'Platform Investigation', text: 'Webhook type-conversion event gate fix.' },
    ],
  },
  {
    date: 'Aug 6, 2026',
    entries: [
      { area: 'Guardrail', text: 'Launched — client tier classification dashboard with configurable T1/T2/T3 thresholds, smart filters, sortable table, pagination and a 1-Pager doc link.' },
      { area: 'Guardrail', text: 'Access controlled via a DB-backed guardrailAccess flag managed in the admin panel.' },
      { area: 'SNYK', text: 'Configurable cron schedule via admin UI; clearer Datadog 403 error messages.' },
    ],
  },
  {
    date: 'Aug 5, 2026',
    entries: [
      { area: 'Reliability & Scale', text: 'Sub-category management — group ADO features by sub-category with configurable tag aliases shown in section headers.' },
      { area: 'Engineering Processes', text: 'New Onboarding tab with sessions plan and architecture diagrams.' },
      { area: 'Engineering Processes', text: 'New Service Management tab with daily IST cron for service status tracking.' },
      { area: 'SNYK', text: 'Manual refresh button, live sync status indicator, 2-hourly IST cron.' },
    ],
  },
  {
    date: 'Aug 4, 2026',
    entries: [
      { area: 'Reliability & Scale', text: 'Complete UI revamp — modern accessible design, SP-weighted progress bars, dual-source data fallback, HTML docs for Programme Overview and checkSystemHealth.' },
      { area: 'Auth', text: 'Auto-redirect to login on any 401 response across all API calls; fix for stale JWT tokens lingering in localStorage.' },
      { area: 'Security', text: 'Fixed 5 critical npm vulnerabilities.' },
    ],
  },
  {
    date: 'Aug 1, 2026',
    entries: [
      { area: 'Release Planning', text: 'Azure Config tab: multi-select filters for Chunk Upload, Workflow Broker, FS Sync and Region; CSV export for the filtered client list.' },
    ],
  },
  {
    date: 'Jul 30–31, 2026',
    entries: [
      { area: 'Release Planning', text: 'New Azure Configuration tab showing instance config (chunk upload, workflow broker, FS sync, version) sourced from CBV.' },
      { area: 'Auth', text: 'Self-service signup with admin approval workflow — users request access, approvers see a pending requests banner.' },
      { area: 'Auth', text: 'Remember Me extended from 1 to 5 days.' },
      { area: 'AI Toolkit', text: 'Live MCP server health chips with a Refresh button.' },
      { area: 'Engineering Memory', text: 'Bi-weekly sprint chart with distinct bar colours, count labels and drill-down into sprint tickets.' },
    ],
  },
  {
    date: 'Jul 28–29, 2026',
    entries: [
      { area: 'Synthetic Tests', text: 'Complete overhaul — HTML report export, rich Teams Adaptive Card notifications, run history table, lazy-load per-run results, delete runs, clickable Most Failed card with breakdown modal.' },
      { area: 'AI Toolkit', text: 'New section added to the portal.' },
    ],
  },
  {
    date: 'Jul 14–27, 2026',
    entries: [
      { area: 'Instance Monitor', text: 'Auto-sync region and VM fields from getCBV on the daily scheduler; Release View syncs customer instances on all getCBV refresh paths.' },
      { area: 'Repositories', text: 'Configurable SonarQube branch per repo (default: develop).' },
      { area: 'Azure Migration', text: 'Recovery trend detection highlights clients improving recently; per-client cards; 2×2 verdict grid.' },
      { area: 'Production Defects', text: 'SLA breakdown added to the dashboard layout.' },
      { area: 'Memory Analysis', text: 'OOM auto-restarts column with filter.' },
      { area: 'UI', text: 'Application-wide responsive layout fixes; default browser zoom set to 80%.' },
      { area: 'Platform Investigation', text: 'Compact aligned card layout.' },
    ],
  },
];

const AREA_COLORS = {
  'Guardrail': '#12365a',
  'Reliability & Scale': '#059669',
  'Reliability': '#059669',
  'SNYK': '#7c3aed',
  'Auth': '#b45309',
  'Security': '#b91c1c',
  'Engineering Processes': '#0891b2',
  'Engineering Memory': '#0891b2',
  'Release Planning': '#1769aa',
  'AI Toolkit': '#6d28d9',
  'Synthetic Tests': '#b45309',
  'Instance Monitor': '#374151',
  'Repositories': '#374151',
  'Azure Migration': '#1769aa',
  'Production Defects': '#b91c1c',
  'Memory Analysis': '#374151',
  'Platform Investigation': '#374151',
  'Service Operations': '#1769aa',
  'UI': '#6d788c',
};

/** Home sidebar sections that require login (Compliance + Platform & Ops) */
const AUTH_REQUIRED_SECTIONS = new Set([
  'security',
  'hipaa-cui',
  'compliance-delivery',
  'h2-2026',
  'accessibility',
  'migrations',
  'sha',
  'service-operations',
  'guardrail',
  'tsi-dashboard',
  'incidents-alerts',
  'ai-toolkit',
  'reliability',
  'operational-excellence',
  'audit-console',
  'confidence-plan',
  // Old deep link; mapped to Confidence Plan → Incident Remediation tab
  'incident-remediation'
]);

/**
 * LandingPage component - Public readonly metrics portal
 * No authentication required
 * @returns {JSX.Element} LandingPage component
 */
const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Get initial section from query parameter or path, default to 'qahealth'
  const getInitialSection = () => {
    const section = searchParams.get('section');
    if (section) {
      if (AUTH_REQUIRED_SECTIONS.has(section) && !isAuthenticated()) {
        return 'qahealth';
      }
      return section;
    }
    // Check if we're on the qahealth route
    if (window.location.pathname === '/qahealth') {
      return 'qahealth';
    }
    // Check if we're on the engineering-processes route
    if (window.location.pathname === '/engineering-processes') {
      return 'engineering-processes';
    }
    if (window.location.pathname === '/internal-bugs') {
      return 'internal-bugs';
    }
    return 'qahealth';
  };

  const [selectedNav, setSelectedNav] = useState(getInitialSection());
  const [deliveryStreamTab, setDeliveryStreamTab] = useState(0); // Index into stream tabs (CoCo Txm, Core, UnMapped, etc.)
  const [deliveryStreamsWithCounts, setDeliveryStreamsWithCounts] = useState([]); // { stream, count }[] for tab labels + badges
  const [deliveryDataLoading, setDeliveryDataLoading] = useState(false);
  const [lastImportInfo, setLastImportInfo] = useState({
    lastImport: null,
    lastImportType: null,
    lastImportStatus: null
  });
  const [importLoading, setImportLoading] = useState(true);
  const [isEventBasedProcessing, setIsEventBasedProcessing] = useState(false);
  const [initialPlanningAdoId, setInitialPlanningAdoId] = useState(null); // New state for ADO ID
  const [activeSessionsCount, setActiveSessionsCount] = useState(0);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [openSessionsDialog, setOpenSessionsDialog] = useState(false);
  const [coverageWindowDays, setCoverageWindowDays] = useState(30);
  const [coverageTreeFilter, setCoverageTreeFilter] = useState(null); // { module?: string, section?: string } | null
  const [changelogOpen, setChangelogOpen] = useState(false);
  // WSRView used to be mounted unconditionally (just hidden via CSS) so its switch was instant.
  // That meant every page load -- including Vital Signs/Operational Excellence -- fired WSRView's
  // bulk `/api/wsr/:id/details` fetch for every WSR item in the background. With enough items that
  // burst of parallel requests can overwhelm the server and starve/timeout unrelated requests on the
  // page actually being viewed (e.g. the Top Customers 504 on the Vital Signs page), and any stale/
  // removed WSR item shows up as a 404 in the network tab even though nothing on screen needed it.
  // Only mount WSRView once the user has actually visited the WSR tab (kept mounted afterwards, same
  // keep-alive pattern used elsewhere on this page, so switching back to WSR stays instant).
  const [wsrVisited, setWsrVisited] = useState(() => getInitialSection() === 'wsr');

  // Send unauthenticated users to login when URL targets a protected section
  useEffect(() => {
    const section = searchParams.get('section');
    if (section && AUTH_REQUIRED_SECTIONS.has(section) && !isAuthenticated()) {
      const redirect = encodeURIComponent(`${location.pathname}${location.search}`);
      navigate(`/login?redirect=${redirect}`, { replace: true });
    }
  }, [searchParams, navigate, location.pathname, location.search]);

  // Update selectedNav and initialPlanningAdoId when URL section/adoId parameter changes
  useEffect(() => {
    const section = searchParams.get('section');
    const adoId = searchParams.get('adoId');
    if (section) {
      if (AUTH_REQUIRED_SECTIONS.has(section) && !isAuthenticated()) {
        setSelectedNav('qahealth');
        setInitialPlanningAdoId(null);
        return;
      }
      setSelectedNav(section === 'incident-remediation' ? 'confidence-plan' : section);
      if (section === 'delivery' && adoId) {
        setDeliveryStreamTab(0);
        setInitialPlanningAdoId(adoId);
      } else {
        setInitialPlanningAdoId(null);
      }
    } else if (window.location.pathname === '/qahealth' || window.location.pathname === '/') {
      setSelectedNav('qahealth');
      setInitialPlanningAdoId(null);
    } else if (window.location.pathname === '/engineering-processes') {
      setSelectedNav('engineering-processes');
      setInitialPlanningAdoId(null);
    }
  }, [searchParams, navigate]);

  const handleNavClick = (navItem, adoId = null) => {
    const section = navItem === 'incident-remediation' ? 'confidence-plan' : navItem;
    setSelectedNav(section);
    let newSearchParams = new URLSearchParams();
    if (section !== 'qahealth') {
      newSearchParams.set('section', section);
    }
    if (section === 'confidence-plan' && navItem === 'incident-remediation') {
      newSearchParams.set('tab', 'incident-remediation');
    }
    if (adoId) {
      newSearchParams.set('adoId', adoId);
    }
    navigate(`/?${newSearchParams.toString()}`, { replace: true });
  };

  // Flip on once the WSR tab is visited; stays mounted (hidden via CSS) afterwards so switching back is instant.
  useEffect(() => {
    if (selectedNav === 'wsr' && !wsrVisited) {
      setWsrVisited(true);
    }
  }, [selectedNav, wsrVisited]);

  const isActive = (navItem) => selectedNav === navItem;

  // Dark green color matching the theme
  const darkGreenColor = "#1D4434";

  // Handle login click - redirect to login with redirect parameter to admin
  const handleLoginClick = () => {
    if (isAuthenticated()) {
      // If already authenticated, go directly to admin panel
      navigate('/admin');
    } else {
      // Redirect to login with redirect parameter
      navigate('/login?redirect=/admin');
    }
  };

  // Handle logout click – navigate in-app so user stays in SPA (no full page reload)
  const handleLogoutClick = () => {
    logout();
    navigate('/');
  };

  // Load processing config to know if we show "Event based - RealTime" or last import time
  useEffect(() => {
    const fetchProcessingConfig = async () => {
      try {
        const response = await apiService.get('/api/processing-config');
        if (response?.success && response?.data) {
          const eventBased = response.data.webhookEnabled && !response.data.batchEnabled;
          setIsEventBasedProcessing(!!eventBased);
        }
      } catch (err) {
        console.error('Error fetching processing config:', err);
      }
    };
    fetchProcessingConfig();
  }, []);

  // Load last import information (only when not event-based; event-based = real-time, no last import stamp)
  useEffect(() => {
    if (isEventBasedProcessing) {
      setImportLoading(false);
      return;
    }
    const fetchLastImportTimes = async () => {
      try {
        setImportLoading(true);
        const response = await apiService.get('/api/import/last-import-times');
        if (response.success) {
          setLastImportInfo({
            lastImport: response.data.lastImport,
            lastImportType: response.data.lastImportType,
            lastImportStatus: response.data.lastImportStatus
          });
        }
      } catch (err) {
        console.error('Error fetching last import times:', err);
      } finally {
        setImportLoading(false);
      }
    };

    fetchLastImportTimes();

    // Refresh the data every hour
    const intervalId = setInterval(fetchLastImportTimes, 60 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [isEventBasedProcessing]);

  // Fetch active sessions count
  useEffect(() => {
    const fetchActivityStats = async () => {
      try {
        setLoadingActivity(true);
        const response = await getActiveSessionsCount();
        console.log('Active sessions count response:', response);
        if (response && response.success) {
          const count = response.count || 0;
          console.log('Setting active sessions count:', count);
          setActiveSessionsCount(count);
        } else {
          console.log('No count in response or response not successful');
          setActiveSessionsCount(0);
        }
      } catch (err) {
        console.error('Error fetching active sessions count:', err);
        // Silently fail - don't show error in header
        setActiveSessionsCount(0);
      } finally {
        setLoadingActivity(false);
      }
    };

    fetchActivityStats();

    // Refresh activity stats every 30 seconds
    const activityIntervalId = setInterval(fetchActivityStats, 30 * 1000);

    return () => clearInterval(activityIntervalId);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch (err) {
      console.error('Error formatting date:', err);
      return 'Invalid date';
    }
  };

  // Load work items + teams when on Delivery section to build stream tabs with counts
  useEffect(() => {
    if (selectedNav !== 'delivery') return;
    let cancelled = false;
    const load = async () => {
      setDeliveryDataLoading(true);
      try {
        const [workItemsRes, teams] = await Promise.all([
          get('/api/workitems/from-epic-hierarchies'),
          getTeamsWithStreams()
        ]);
        if (cancelled) return;
        const workItems = Array.isArray(workItemsRes?.data) ? workItemsRes.data : [];
        const teamList = Array.isArray(teams) ? teams : [];
        const teamToStream = new Map();
        teamList.forEach(t => {
          const stream = t.stream || 'UnMapped';
          if (t.id) teamToStream.set(t.id, stream);
          if (t.displayName) teamToStream.set(t.displayName, stream);
        });
        const getStream = (team) => {
          if (!team || team === 'N/A' || String(team).trim() === '') return 'UnMapped';
          return teamToStream.get(team) || 'UnMapped';
        };
        const countByStream = new Map();
        workItems.forEach(epic => {
          const stream = getStream(epic.team);
          countByStream.set(stream, (countByStream.get(stream) || 0) + 1);
        });
        const streamSet = new Set(countByStream.keys());
        streamSet.add('UnMapped');
        const streamsSorted = Array.from(streamSet).sort();
        setDeliveryStreamsWithCounts(streamsSorted.map(stream => ({
          stream,
          count: countByStream.get(stream) || 0
        })));
      } catch (err) {
        if (!cancelled) setDeliveryStreamsWithCounts([]);
      } finally {
        if (!cancelled) setDeliveryDataLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedNav]);

  // Render the Delivery tab content: one tab per stream (with count badge), then quarters + To be Planned under each
  const renderDeliveryTab = () => {
    const selectedStream = deliveryStreamsWithCounts[deliveryStreamTab]?.stream ?? null;
    return (
      <Box>
        <Tabs
          value={Math.min(deliveryStreamTab, Math.max(0, deliveryStreamsWithCounts.length - 1))}
          onChange={(e, v) => setDeliveryStreamTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 3 }}
        >
          {deliveryStreamsWithCounts.map(({ stream, count }, idx) => (
            <Tab
              key={stream}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{stream}</span>
                  <Chip label={count} size="small" sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 600 }} />
                </Box>
              }
            />
          ))}
        </Tabs>
        {deliveryDataLoading && deliveryStreamsWithCounts.length === 0 ? (
          <Typography color="textSecondary">Loading streams...</Typography>
        ) : selectedStream ? (
          <WorkItemsView streamName={selectedStream} initialPlanningAdoId={initialPlanningAdoId} />
        ) : null}
      </Box>
    );
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        backgroundColor: 'background.default',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header Section */}
      <Box 
        sx={{ 
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          py: 3,
          boxShadow: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 500 }}>
            Metrics Portal
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            Readonly metrics and analytics dashboard
          </Typography>
        </Box>
        <Box display="flex" gap={3} alignItems="center" flexWrap="wrap">
          {/* Application Summary link – top right */}
          <Button
            component={Link}
            to="/summary"
            startIcon={<SummarizeIcon />}
            sx={{
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
            variant="outlined"
          >
            Summary
          </Button>
          <Tooltip title="What's new" arrow>
            <IconButton
              onClick={() => setChangelogOpen(true)}
              sx={{
                color: 'white',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '8px',
                p: 0.75,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', borderColor: 'white' },
              }}
            >
              <ChangelogIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          {/* Active Sessions Chip - Always visible and clickable */}
          <Chip
            label={loadingActivity ? 'Loading...' : `${activeSessionsCount} Active Session${activeSessionsCount !== 1 ? 's' : ''}`}
            size="small"
            onClick={() => setOpenSessionsDialog(true)}
            sx={{
              bgcolor: activeSessionsCount > 0 ? '#ff9800' : '#4caf50',
              color: '#FFFFFF !important',
              fontSize: '0.8rem',
              height: '28px',
              minWidth: '120px',
              fontWeight: 700,
              border: '1px solid rgba(255, 255, 255, 0.5)',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.9,
                transform: 'scale(1.05)',
                transition: 'all 0.2s ease-in-out'
              },
              '& .MuiChip-label': {
                px: 1.5,
                color: '#FFFFFF !important',
                fontWeight: 700,
                fontSize: '0.8rem',
                textTransform: 'none'
              }
            }}
          />
          
          {/* Active Sessions Dialog */}
          <ActiveSessionsDialog
            open={openSessionsDialog}
            onClose={() => setOpenSessionsDialog(false)}
          />
          
          {/* Last Import or Event-based indicator */}
          {isEventBasedProcessing ? (
            <Typography variant="body2" sx={{ color: '#FFFFFF', fontSize: '0.875rem', fontWeight: 500 }}>
              Event based - RealTime
            </Typography>
          ) : !importLoading && lastImportInfo.lastImport ? (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5,
              flexWrap: 'wrap'
            }}>
              <Typography variant="body2" sx={{ color: '#FFFFFF', fontSize: '0.875rem', fontWeight: 500 }}>
                Last {lastImportInfo.lastImportType === 'delta' ? 'Delta' : 'Full'} Import:
              </Typography>
              <Typography variant="body2" sx={{ color: '#FFFFFF', fontSize: '0.875rem' }}>
                {formatDate(lastImportInfo.lastImport)}
              </Typography>
              {lastImportInfo.lastImportStatus && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.875rem',
                    color: lastImportInfo.lastImportStatus === 'success' ? '#81c784' : 
                           lastImportInfo.lastImportStatus === 'failed' ? '#e57373' : '#ffb74d',
                    fontStyle: 'italic',
                    fontWeight: 500
                  }}
                >
                  ({lastImportInfo.lastImportStatus})
                </Typography>
              )}
            </Box>
          ) : null}
          {/* Buttons */}
          <Box display="flex" gap={2} alignItems="center">
            {isAuthenticated() && (
              <Button
                variant="outlined"
                startIcon={<LogoutIcon />}
                onClick={handleLogoutClick}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Logout
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<LoginIcon />}
              onClick={handleLoginClick}
              sx={{
                backgroundColor: 'white',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'grey.100'
                }
              }}
            >
              {isAuthenticated() ? 'Go to Admin' : 'Login'}
            </Button>
          </Box>
        </Box>
      </Box>

      <PendingRequestsBanner />

      {/* Main Content Area with Sidebar */}
      <Box
        display="flex"
        flexGrow={1}
        overflow="hidden"
        sx={{ alignItems: 'stretch', minHeight: 0 }}
      >
        {/* Left Sidebar Navigation */}
        <Paper 
          elevation={2} 
          sx={{ 
            width: 250, 
            minWidth: 250,
            maxWidth: 250,
            alignSelf: 'stretch',
            minHeight: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            borderRadius: 0,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            '& .MuiListItem-root': { overflow: 'hidden' },
            '& .MuiListItemText-root': { minWidth: 0 },
          }}
        >
          <List
            component="nav"
            sx={{
              pt: 1,
              pb: 12,
              flex: 1,
              minHeight: '100%',
              boxSizing: 'border-box'
            }}
          >
            {/* Health Dashboard */}
            <ListItem 
              button 
              selected={isActive('qahealth')}
              onClick={() => handleNavClick('qahealth')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }
              }}
            >
              <ListItemIcon>
                <HealthIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="Health Dashboard" />
            </ListItem>

            {/* AI Toolkit — login required */}
            {isAuthenticated() && (
              <>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ padding: '16px 16px 0', display: 'block' }}
                >
                  AI Toolkit
                </Typography>

                <ListItem
                  button
                  selected={isActive('ai-toolkit')}
                  onClick={() => handleNavClick('ai-toolkit')}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'action.selected',
                      '&:hover': { backgroundColor: 'action.selected' }
                    }
                  }}
                >
                  <ListItemIcon>
                    <AIToolkitIcon sx={{ color: darkGreenColor }} />
                  </ListItemIcon>
                  <ListItemText primary="AI Toolkit" />
                </ListItem>
              </>
            )}

            {/* Delivery Section */}
            <Typography 
              variant="overline" 
              color="text.secondary"
              sx={{ 
                padding: '16px 16px 0', 
                display: 'block'
              }}
            >
              Delivery
            </Typography>
            
            <ListItem 
              button 
              selected={isActive('delivery')}
              onClick={() => handleNavClick('delivery')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }
              }}
            >
              <ListItemIcon>
                <DashboardIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="Planning" />
            </ListItem>

            <ListItem
              button
              selected={isActive('repositories')}
              onClick={() => handleNavClick('repositories')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }
              }}
            >
              <ListItemIcon>
                <RepositoriesIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="Repositories" />
            </ListItem>

            <ListItem 
              button 
              selected={isActive('production-defects')}
              onClick={() => handleNavClick('production-defects')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }
              }}
            >
              <ListItemIcon>
                <BugIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="Production Bugs" />
            </ListItem>

            <ListItem
              button
              selected={isActive('internal-bugs')}
              onClick={() => handleNavClick('internal-bugs')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': { backgroundColor: 'action.selected' }
                }
              }}
            >
              <ListItemIcon>
                <BugIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="Internal Bugs" />
            </ListItem>

            <ListItem
              button
              selected={isActive('scrum-metrics')}
              onClick={() => handleNavClick('scrum-metrics')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': { backgroundColor: 'action.selected' }
                }
              }}
            >
              <ListItemIcon>
                <ScrumMetricsIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="Scrum Metrics" />
            </ListItem>

            <ListItem
              button
              selected={isActive('engineering-processes')}
              onClick={() => handleNavClick('engineering-processes')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }
              }}
            >
              <ListItemIcon>
                <EngineeringIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="Engineering Processes" />
            </ListItem>

            <ListItem 
              button 
              selected={isActive('releases')}
              onClick={() => handleNavClick('releases')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }
              }}
            >
              <ListItemIcon>
                <ReleaseIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="Releases" />
            </ListItem>

            <ListItem 
              button 
              selected={isActive('wsr')}
              onClick={() => handleNavClick('wsr')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }
              }}
            >
              <ListItemIcon>
                <WSRIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="WSR" />
            </ListItem>

            {hasOperationalExcellenceAccess() && (
              <>
            {/* Operational Excellence Section */}
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{
                padding: '16px 16px 0',
                display: 'block'
              }}
            >
              Operational Excellence
            </Typography>

            <ListItem
              button
              selected={isActive('operational-excellence')}
              onClick={() => handleNavClick('operational-excellence')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': { backgroundColor: 'action.selected' }
                }
              }}
            >
              <ListItemIcon>
                <CustomerIssueIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="HighQ Vital Signs" />
            </ListItem>

            <ListItem
              button
              selected={isActive('audit-console')}
              onClick={() => handleNavClick('audit-console')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': { backgroundColor: 'action.selected' }
                }
              }}
            >
              <ListItemIcon>
                <AuditConsoleIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="HighQ Audit Console" />
            </ListItem>

            <ListItem
              button
              selected={isActive('confidence-plan')}
              onClick={() => handleNavClick('confidence-plan')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': { backgroundColor: 'action.selected' }
                }
              }}
            >
              <ListItemIcon>
                <ConfidencePlanIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="Confidence Plan" />
            </ListItem>
              </>
            )}

            {/* QA Section */}
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{
                padding: '16px 16px 0',
                display: 'block'
              }}
            >
              QA
            </Typography>

            <ListItem 
              button 
              selected={isActive('tests')}
              onClick={() => handleNavClick('tests')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }
              }}
            >
              <ListItemIcon>
                <TestsIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="Tests" />
            </ListItem>

            <ListItem
              button
              selected={isActive('automation')}
              onClick={() => handleNavClick('automation')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }
              }}
            >
              <ListItemIcon>
                <AutomationIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="Automation" />
            </ListItem>

            {/* Pull Requests nav item hidden — functionality moved to Automation > Daily Regression */}

            <ListItem
              button
              selected={isActive('test-plans')}
              onClick={() => handleNavClick('test-plans')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }
              }}
            >
              <ListItemIcon>
                <TestsIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="Test Plans" />
            </ListItem>

            <ListItem
              button
              selected={isActive('test-instances')}
              onClick={() => handleNavClick('test-instances')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }
              }}
            >
              <ListItemIcon>
                <TestInstanceIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="Test Instances" />
            </ListItem>

            <ListItem 
              button 
              selected={isActive('performance')}
              onClick={() => handleNavClick('performance')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }
              }}
            >
              <ListItemIcon>
                <PerformanceIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="Performance" />
            </ListItem>

            <ListItem
              button
              selected={isActive('code-coverage')}
              onClick={() => handleNavClick('code-coverage')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }
              }}
            >
              <ListItemIcon>
                <CoverageIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="Code Coverage" />
            </ListItem>

            {/* Compliance — login required */}
            {isAuthenticated() && (
              <>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{
                padding: '16px 16px 0',
                display: 'block'
              }}
            >
              Compliance
            </Typography>

            <ListItem 
              button 
              selected={isActive('security')}
              onClick={() => handleNavClick('security')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }
              }}
            >
              <ListItemIcon>
                <SecurityIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="SNYK" />
            </ListItem>

            <ListItem
              button
              selected={isActive('compliance-delivery') || isActive('hipaa-cui') || isActive('h2-2026')}
              onClick={() => handleNavClick('compliance-delivery')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }
              }}
            >
              <ListItemIcon>
                <HipaaCuiIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText
                primary="Compliance Delivery"
                secondary="HIPAA/CUI · H2-2026"
                secondaryTypographyProps={{ variant: 'caption', sx: { opacity: 0.85, lineHeight: 1.3 } }}
              />
            </ListItem>

            <ListItem
              button 
              selected={isActive('accessibility')}
              onClick={() => handleNavClick('accessibility')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }
              }}
            >
              <ListItemIcon>
                <ComplianceIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="Accessibility" />
            </ListItem>
              </>
            )}

            {/* Platform & Ops — login required */}
            {isAuthenticated() && (
              <>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{
                padding: '16px 16px 0',
                display: 'block'
              }}
            >
              Platform & Ops
            </Typography>

            <ListItem
              button
              selected={isActive('release-calendar')}
              onClick={() => handleNavClick('release-calendar')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }
              }}
            >
              <ListItemIcon>
                <CalendarMonthIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText
                primary="Release planning"
                secondary="Calendar · instances · production analysis"
                secondaryTypographyProps={{ variant: 'caption', sx: { opacity: 0.85, lineHeight: 1.3 } }}
              />
            </ListItem>

            <ListItem
              button
              selected={isActive('migrations')}
              onClick={() => handleNavClick('migrations')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }
              }}
            >
              <ListItemIcon>
                <MigrationsIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="Migrations" />
            </ListItem>

            <ListItem
              button
              selected={isActive('sha')}
              onClick={() => handleNavClick('sha')}
              title="SHA — Service Health Assessment"
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }
              }}
            >
              <ListItemIcon>
                <ShaIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText
                primary="SHA"
                secondary="Service Health Assessment"
                secondaryTypographyProps={{ variant: 'caption', sx: { opacity: 0.85, lineHeight: 1.3 } }}
              />
            </ListItem>

            <ListItem
              button
              selected={isActive('reliability')}
              onClick={() => handleNavClick('reliability')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }
              }}
            >
              <ListItemIcon>
                <ReliabilityIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText
                primary="Reliability & Scale"
                secondary="Reliability & Scale Program"
                secondaryTypographyProps={{ variant: 'caption', sx: { opacity: 0.85, lineHeight: 1.3 } }}
              />
            </ListItem>

            <ListItem
              button
              selected={isActive('service-operations')}
              onClick={() => handleNavClick('service-operations')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': { backgroundColor: 'action.selected' }
                }
              }}
            >
              <ListItemIcon>
                <PlatformInvestigationIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="Service Operations" />
            </ListItem>

            {getCurrentUser()?.guardrailAccess === true && (
              <ListItem
                button
                selected={isActive('guardrail')}
                onClick={() => handleNavClick('guardrail')}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'action.selected',
                    '&:hover': { backgroundColor: 'action.selected' }
                  }
                }}
              >
                <ListItemIcon>
                  <GuardrailIcon sx={{ color: darkGreenColor }} />
                </ListItemIcon>
                <ListItemText primary="Guardrail" />
              </ListItem>
            )}

            <ListItem
              button
              selected={isActive('tsi-dashboard')}
              onClick={() => handleNavClick('tsi-dashboard')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }
              }}
            >
              <ListItemIcon>
                <TsiIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="TSI Dashboard" />
            </ListItem>

            <ListItem
              button
              selected={isActive('incidents-alerts')}
              onClick={() => handleNavClick('incidents-alerts')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }
              }}
            >
              <ListItemIcon>
                <OpsCenterIcon sx={{ color: darkGreenColor }} />
              </ListItemIcon>
              <ListItemText primary="Major Incidents" />
            </ListItem>

              </>
            )}

          </List>
        </Paper>

        {/* Main Content Area */}
        <Box 
          component="main" 
          flexGrow={1}
          bgcolor="background.default"
          overflow="auto"
          sx={{
            minWidth: 0,
            overflowX: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box sx={{ flexGrow: 1, p: 3 }}>
            {selectedNav === 'qahealth' && (
              <Box data-testid="qa-health-dashboard">
                <QAHealthDashboard />
              </Box>
            )}
            {selectedNav === 'delivery' && renderDeliveryTab()}
            {wsrVisited && (
              <Box sx={{ display: selectedNav === 'wsr' ? 'block' : 'none' }}>
                <WSRView />
              </Box>
            )}
            {selectedNav === 'production-defects' && (
              <ProductionDefects />
            )}
            {selectedNav === 'internal-bugs' && (
              <InternalBugsPublic />
            )}
            {selectedNav === 'scrum-metrics' && (
              <SprintMetricsView />
            )}
            {selectedNav === 'tsi-dashboard' && isAuthenticated() && (
              <TsiDashboard />
            )}
            {selectedNav === 'repositories' && (
              <RepositoriesView />
            )}
            {selectedNav === 'recommended-tests' && (
              <RecommendedTestsView />
            )}
            {selectedNav === 'automation' && (
              <AutomationTab />
            )}
            {selectedNav === 'security' && isAuthenticated() && (
              <SecurityPage />
            )}
            {selectedNav === 'compliance-delivery' && isAuthenticated() && (
              <ComplianceDeliveryLanding onNavigate={handleNavClick} />
            )}
            {selectedNav === 'hipaa-cui' && isAuthenticated() && (
              <HipaaCuiDashboard />
            )}
            {selectedNav === 'h2-2026' && isAuthenticated() && (
              <H22026Dashboard />
            )}
            {selectedNav === 'accessibility' && isAuthenticated() && (
              <Compliance />
            )}
            {selectedNav === 'performance' && (
              <PerformanceView />
            )}
            {selectedNav === 'releases' && (
              <PublicReleasesView />
            )}
            {selectedNav === 'tests' && (
              <Tests />
            )}
            {selectedNav === 'migrations' && isAuthenticated() && (
              <MigrationsView />
            )}
            {selectedNav === 'sha' && isAuthenticated() && (
              <SHAView />
            )}
            {selectedNav === 'service-operations' && isAuthenticated() && (
              <PlatformInvestigationView />
            )}
            {selectedNav === 'guardrail' && isAuthenticated() && (
              <GuardrailView />
            )}
            {selectedNav === 'incidents-alerts' && isAuthenticated() && (
              <OpsCenter />
            )}
            {selectedNav === 'operational-excellence' && isAuthenticated() && hasOperationalExcellenceAccess() && (
              <OperationalExcellenceHub />
            )}
            {selectedNav === 'audit-console' && isAuthenticated() && hasOperationalExcellenceAccess() && (
              <AuditConsolePage />
            )}
            {selectedNav === 'confidence-plan' && isAuthenticated() && hasOperationalExcellenceAccess() && (
              <ConfidencePlanPage />
            )}
            {selectedNav === 'reliability' && isAuthenticated() && (
              <ReliabilityDashboard />
            )}
            {selectedNav === 'engineering-processes' && (
              <EngineeringProcessesView />
            )}
            {selectedNav === 'ai-toolkit' && isAuthenticated() && (
              <AIToolkitView />
            )}
            {selectedNav === 'test-plans' && (
              <TestPlanGenerator />
            )}
            {selectedNav === 'test-instances' && (
              <TestInstancesView />
            )}
            {selectedNav === 'code-coverage' && (
              <CoverageLanding
                onOpenTree={(w, filter) => {
                  if (typeof w === 'number') setCoverageWindowDays(w);
                  setCoverageTreeFilter(filter || null);
                  handleNavClick('code-coverage-tree');
                }}
                initialWindowDays={coverageWindowDays}
                onWindowChange={setCoverageWindowDays}
              />
            )}
            {selectedNav === 'code-coverage-tree' && (
              <CoverageTreeView
                onBackToLanding={() => handleNavClick('code-coverage')}
                initialWindowDays={coverageWindowDays}
                initialFilter={coverageTreeFilter}
              />
            )}
            {selectedNav === 'release-calendar' && (
              <ReleaseCalendarView />
            )}
          </Box>
        </Box>
      </Box>

      {/* Changelog modal */}
      <Dialog
        open={changelogOpen}
        onClose={() => setChangelogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxHeight: '80vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid #dfe5ee' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChangelogIcon sx={{ color: '#12365a', fontSize: 22 }} />
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#12365a' }}>What's New</Typography>
          </Box>
          <IconButton size="small" onClick={() => setChangelogOpen(false)} sx={{ color: '#6d788c' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, pt: 2, pb: 2 }}>
          {CHANGELOG.map((section, si) => (
            <Box key={si} sx={{ mb: 2.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6d788c', letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1 }}>
                {section.date}
              </Typography>
              {section.entries.map((entry, ei) => (
                <Box key={ei} sx={{ display: 'flex', gap: 1, mb: 0.75, alignItems: 'flex-start' }}>
                  <Chip
                    label={entry.area}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      flexShrink: 0,
                      mt: '2px',
                      bgcolor: `${AREA_COLORS[entry.area] || '#374151'}18`,
                      color: AREA_COLORS[entry.area] || '#374151',
                      border: `1px solid ${AREA_COLORS[entry.area] || '#374151'}30`,
                    }}
                  />
                  <Typography sx={{ fontSize: '0.82rem', color: '#374151', lineHeight: 1.5 }}>
                    {entry.text}
                  </Typography>
                </Box>
              ))}
              {si < CHANGELOG.length - 1 && <Divider sx={{ mt: 2 }} />}
            </Box>
          ))}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LandingPage;
